/* tslint:disable */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { DefaultHttpOptions, HttpOptions, APIClientInterface } from './';

import * as models from './models';

export const USE_DOMAIN = new InjectionToken<string>('APIClient_USE_DOMAIN');
export const USE_HTTP_OPTIONS = new InjectionToken<HttpOptions>('APIClient_USE_HTTP_OPTIONS');

type APIHttpOptions = HttpOptions & {
  headers: HttpHeaders;
  params: HttpParams;
  responseType?: 'arraybuffer' | 'blob' | 'text' | 'json';
};

/**
 * Created with https://github.com/flowup/api-client-generator
 */
@Injectable()
export class APIClient implements APIClientInterface {

  readonly options: APIHttpOptions;

  readonly domain: string = `//${window.location.hostname}${window.location.port ? ':'+window.location.port : ''}/api/v1`;

  constructor(private readonly http: HttpClient,
              @Optional() @Inject(USE_DOMAIN) domain?: string,
              @Optional() @Inject(USE_HTTP_OPTIONS) options?: DefaultHttpOptions) {

    if (domain != null) {
      this.domain = domain;
    }

    this.options = {
      headers: new HttpHeaders(options && options.headers ? options.headers : {}),
      params: new HttpParams(options && options.params ? options.params : {}),
      ...(options && options.reportProgress ? { reportProgress: options.reportProgress } : {}),
      ...(options && options.withCredentials ? { withCredentials: options.withCredentials } : {})
    };
  }

  /**
   * Rendszerparaméterek lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getSystemSettings(
    requestHttpOptions?: HttpOptions
  ): Observable<models.SystemSettingsDto> {
    const path = `/system/settings`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.SystemSettingsDto>('GET', path, options);
  }

  /**
   * Szerződések lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContracts(
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractDto[]> {
    const path = `/contracts`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ContractDto[]>('GET', path, options);
  }

  /**
   * Szerződés részletes adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractById(
    args: {
      iD: number,  // Szerződés azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractDto> {
    const path = `/contracts/${args.iD}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ContractDto>('GET', path, options);
  }

  /**
   * Szerződések lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractsSearchList(
    args: {
      koopFrameContractID?: string,  // (optional) Keretszerződés szám (kötvényszám)
      productTypeSID?: string,  // (optional) Szerződés típus azonosítója
      stateSID?: string,  // (optional) Szerződés állapot azonosítója
      contractorName?: string,  // (optional) Szerződő neve
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractSearchListItemDto[]> {
    const path = `/contracts/search`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('koopFrameContractID' in args) {
      options.params = options.params.set('koopFrameContractID', String(args.koopFrameContractID));
    }
    if ('productTypeSID' in args) {
      options.params = options.params.set('productTypeSID', String(args.productTypeSID));
    }
    if ('stateSID' in args) {
      options.params = options.params.set('stateSID', String(args.stateSID));
    }
    if ('contractorName' in args) {
      options.params = options.params.set('contractorName', String(args.contractorName));
    }
    return this.sendRequest<models.ContractSearchListItemDto[]>('GET', path, options);
  }

  /**
   * Biztosítottak listájának  frissítése szerződéshez
   * Response generated for [ 200 ] HTTP response code.
   */
  postContractsInsurantsUpload(
    args: {
      insurantsData: models.ContractsInsurantsUploadDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.UFOFileInfoResultDto> {
    const path = `/contracts/insurants/upload`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.UFOFileInfoResultDto>('POST', path, options, JSON.stringify(args.insurantsData));
  }

  /**
   * Ügynök adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractsAgent(
    args: {
      contractId: number,  // Szerződés belső azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.AgentDto> {
    const path = `/contracts/${args.contractId}/agent`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.AgentDto>('GET', path, options);
  }

  /**
   * Alszerződés részletes létszám adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getManpowerReportBySubContractId(
    args: {
      subContractId: number,  // Alszerződés azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ManpowerReportDto[]> {
    const path = `/contracts/manpower/${args.subContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ManpowerReportDto[]>('GET', path, options);
  }

  /**
   * Alszerződés részletes létszám adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getManpowerReportByKoopSubContractId(
    args: {
      koopSubContractId: number,  // Alszerződés KOOP azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ManpowerReportDto[]> {
    const path = `/contracts/manpower/external/${args.koopSubContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ManpowerReportDto[]>('GET', path, options);
  }

  /**
   * Paraméterben megadott aktív keretszerződés azonosító alapján a kapcsolódó passzív keretszerződések lekérdezése a ContractToContract táblából
   * Response generated for [ 200 ] HTTP response code.
   */
  getConnectedContracts(
    args: {
      iD: number,  // Aktív keretszerződés azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractToContractDto[]> {
    const path = `/contracts/${args.iD}/passiveContracts`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ContractToContractDto[]>('GET', path, options);
  }

  /**
   * Paraméterben megadott aktív keretszerződés azonosító alapján a kapcsolódó passzív keretszerződések mentése a ContractToContract táblába
   * Response generated for [ 200 ] HTTP response code.
   */
  saveConnectedContracts(
    args: {
      iD: number,  // Aktív keretszerződés azonosítója
      connectedContracts: any,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractToContractDto[]> {
    const path = `/contracts/${args.iD}/passiveContracts`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ContractToContractDto[]>('POST', path, options, JSON.stringify(args.connectedContracts));
  }

  /**
   * Paraméterben megadott connection key alapján a kapcsolódó PassiveContractAdministration tábla adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractAdministrationDataByConnectionKey(
    args: {
      connectionKey: string,  // Passzív keretszerződés koop-os azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PassiveContractAdministrationDto> {
    const path = `/contracts/administrationData/${args.connectionKey}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.PassiveContractAdministrationDto>('GET', path, options);
  }

  /**
   * Paraméterben megadott passzív keretszerződés azonosító alapján a kapcsolódó PassiveContractAdministration tábla adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractAdministrationData(
    args: {
      koopPassiveFrameContractId: string,  // Passzív keretszerződés koop-os azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PassiveContractAdministrationDto> {
    const path = `/contracts/${args.koopPassiveFrameContractId}/administrationData`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.PassiveContractAdministrationDto>('GET', path, options);
  }

  /**
   * Paraméterben megadott passzív keretszerződés azonosító alapján a logó mentése
   * Response generated for [ 200 ] HTTP response code.
   */
  saveContractAdministrationData(
    args: {
      koopPassiveFrameContractId: string,  // Passzív keretszerződés koop-os azonosítója
      passiveContractAdministration: models.PassiveContractAdministrationDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PassiveContractAdministrationDto> {
    const path = `/contracts/${args.koopPassiveFrameContractId}/administrationData`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.PassiveContractAdministrationDto>('PUT', path, options, JSON.stringify(args.passiveContractAdministration));
  }

  /**
   * Paraméterben megadott passzív keretszerződés azonosító alapján a keretszerződés lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getPassiveContract(
    args: {
      connectionKey: string,  // Passzív keretszerződés koop-os azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPContractDto> {
    const path = `/contracts/passiveContracts/${args.connectionKey}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.HIPContractDto>('GET', path, options);
  }

  /**
   * Ellenőrízzük, hogy a szerződő egyedi azonosítója szerepel e már az adatbázisban, hogy kiszürjük a duplikációt
   * Response generated for [ 200 ] HTTP response code.
   */
  checkDuplicateDocumentNumbers(
    args: {
      koopFrameContractId: string,  // Keretszerződés szám (kötvényszám)
      socialSecurityNumber?: string,  // (optional) TAJ szám
      taxNumber?: string,  // (optional) Adószám
      passport?: string,  // (optional) Útlevél szám
    },
    requestHttpOptions?: HttpOptions
  ): Observable<string> {
    const path = `/contracts/passiveContracts/checkDuplicateDocumentNumbers`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('koopFrameContractId' in args) {
      options.params = options.params.set('koopFrameContractId', String(args.koopFrameContractId));
    }
    if ('socialSecurityNumber' in args) {
      options.params = options.params.set('socialSecurityNumber', String(args.socialSecurityNumber));
    }
    if ('taxNumber' in args) {
      options.params = options.params.set('taxNumber', String(args.taxNumber));
    }
    if ('passport' in args) {
      options.params = options.params.set('passport', String(args.passport));
    }
    return this.sendRequest<string>('GET', path, options);
  }

  /**
   * Fizetés státusz beállítása az InsuredPersonFromHIP táblában
   * Response generated for [ 200 ] HTTP response code.
   */
  setPaymentStatus(
    args: {
      insuredPersonFromHIPId?: number,  // (optional) InsuredPersonFromHIP táblában található azonosító
      offerNumber?: string,  // (optional) InsuredPersonFromHIP táblában található azonosító
      paymentCode?: string,  // (optional) Az InsuredPersonFromHIP táblában található mező állapota
      state: string,  // Az InsuredPersonFromHIP táblában található mező állapota
    },
    requestHttpOptions?: HttpOptions
  ): Observable<void> {
    const path = `/contracts/passiveContracts/insuredPerson`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('insuredPersonFromHIPId' in args) {
      options.params = options.params.set('insuredPersonFromHIPId', String(args.insuredPersonFromHIPId));
    }
    if ('offerNumber' in args) {
      options.params = options.params.set('offerNumber', String(args.offerNumber));
    }
    if ('paymentCode' in args) {
      options.params = options.params.set('paymentCode', String(args.paymentCode));
    }
    if ('state' in args) {
      options.params = options.params.set('state', String(args.state));
    }
    return this.sendRequest<void>('PATCH', path, options);
  }

  /**
   * Ajánlatszám kérése és fizetés
   * Response generated for [ 200 ] HTTP response code.
   */
  payInsuredPersonFromHip(
    args: {
      insuredPersonFromHIPId: number,  // InsuredPersonFromHIP táblában található azonosító
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.StringResponseDto> {
    const path = `/contracts/passiveContracts/${args.insuredPersonFromHIPId}/insuredPerson`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.StringResponseDto>('POST', path, options);
  }

  /**
   * A fizetés státuszának lekérése OnlinePaymentApi felületéről és beállítása a PBO adatbázisában
   * Response generated for [ 200 ] HTTP response code.
   */
  processPaymentResponse(
    args: {
      providerData: any,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.StringResponseDto> {
    const path = `/contracts/passiveContracts/insuredPerson/payment`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.StringResponseDto>('POST', path, options, JSON.stringify(args.providerData));
  }

  /**
   * Szerződés elküldése a SmartCube felé
   * Response generated for [ 200 ] HTTP response code.
   */
  sendContractFromHipById(
    args: {
      insuredPersonFromHIPId: number,  // InsuredPersonFromHIP táblában található azonosító
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.SmartCubePDFGenerationResponseDto> {
    const path = `/contracts/passiveContracts/insuredPerson/pdf/${args.insuredPersonFromHIPId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.SmartCubePDFGenerationResponseDto>('POST', path, options);
  }

  /**
   * Címek lekérdezése több lépcsőben
   * Response generated for [ 200 ] HTTP response code.
   */
  getAddress(
    args: {
      zip: number,  // Irányítószám
      city?: string,  // (optional) Város
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.CentralZIPDto[]> {
    const path = `/contracts/address`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('zip' in args) {
      options.params = options.params.set('zip', String(args.zip));
    }
    if ('city' in args) {
      options.params = options.params.set('city', String(args.city));
    }
    return this.sendRequest<models.CentralZIPDto[]>('GET', path, options);
  }

  /**
   * Keretszerződéshez tartozó kiegészítő információk lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getContractProcessingDataByContractId(
    args: {
      iD: number,  // Keretszerződés azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.ContractProcessingDataDto> {
    const path = `/contracts/information/${args.iD}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ContractProcessingDataDto>('GET', path, options);
  }

  /**
   * Az adott azonosítójú szerződés előkészítése exportálásra
   * Response generated for [ 200 ] HTTP response code.
   */
  insertExportQueueByContract(
    args: {
      contractId: number,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.StringResponseDto> {
    const path = `/contracts/export/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('contractId' in args) {
      options.params = options.params.set('contractId', String(args.contractId));
    }
    return this.sendRequest<models.StringResponseDto>('POST', path, options);
  }

  /**
   * Biztosítottak listájának lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurants(
    args: {
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PagedNaturalPersonDto> {
    const path = `/insurants`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.PagedNaturalPersonDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosított adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantById(
    args: {
      iD: string,  // Biztosított azonosítója (NaturalPersonId || <keret>_<al>_<partner>))
      isPassive?: boolean,  // (optional) A biztosított passzív?
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.NaturalPersonDto> {
    const path = `/insurants/${args.iD}/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('isPassive' in args) {
      options.params = options.params.set('isPassive', String(args.isPassive));
    }
    return this.sendRequest<models.NaturalPersonDto>('GET', path, options);
  }

  /**
   * Biztosított adatainak módosítása
   * Response generated for [ 200 ] HTTP response code.
   */
  patchInsurantById(
    args: {
      iD: number,  // biztosított azonosítója
      insurantsData: models.NaturalPersonDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.NaturalPersonDto> {
    const path = `/insurants/${args.iD}/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.NaturalPersonDto>('PATCH', path, options, JSON.stringify(args.insurantsData));
  }

  /**
   * Biztosított adatainak kényszerített módosítása
   * Response generated for [ 200 ] HTTP response code.
   */
  putInsurantById(
    args: {
      iD: number,  // biztosított azonosítója
      validatedNaturalPersonDto: models.ValidatedNaturalPersonDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.NaturalPersonDto> {
    const path = `/insurants/${args.iD}/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.NaturalPersonDto>('PUT', path, options, JSON.stringify(args.validatedNaturalPersonDto));
  }

  /**
   * Új biztosított felvétele
   * Response generated for [ 200 ] HTTP response code.
   */
  createInsurant(
    args: {
      insurantsData: models.NaturalPersonDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.NaturalPersonDto> {
    const path = `/insurants/create`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.NaturalPersonDto>('POST', path, options, JSON.stringify(args.insurantsData));
  }

  /**
   * Új biztosított felvétele (figyelmeztetésekkel)
   * Response generated for [ 200 ] HTTP response code.
   */
  forceCreateInsurant(
    args: {
      validatedNaturalPersonDto: models.ValidatedNaturalPersonDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.NaturalPersonDto> {
    const path = `/insurants/create`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.NaturalPersonDto>('PUT', path, options, JSON.stringify(args.validatedNaturalPersonDto));
  }

  /**
   * Biztosítottak keresési lista
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsSearchList(
    args: {
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PagedInsurantsSearchListItemDto> {
    const path = `/insurants/search`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.PagedInsurantsSearchListItemDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosítottak listájának lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsSearchListByFrameContractId(
    args: {
      frameId: number,  // keretszerződés azonosítója
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PagedInsurantsSearchListItemDto> {
    const path = `/insurants/search/${args.frameId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.PagedInsurantsSearchListItemDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosítottak listájának lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsSearchListByFrameAndSubContractId(
    args: {
      frameId: number,  // keretszerződés azonosítója
      subId: number,  // alszerződés azonosítója
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.PagedInsurantsSearchListItemDto> {
    const path = `/insurants/search/${args.frameId}/${args.subId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.PagedInsurantsSearchListItemDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosított törlése
   * Response generated for [ 204 ] HTTP response code.
   */
  deleteInsurant(
    args: {
      insurantsData: models.NaturalPersonDto,
      insurantsId: number,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<void> {
    const path = `/insurants/delete/${args.insurantsId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<void>('DELETE', path, options, JSON.stringify(args.insurantsData));
  }

  /**
   * Biztosítottak listájának exportja
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsExportByFrameAndSubContractId(
    args: {
      frameId: number,  // keretszerződés azonosítója
      subId: number,  // alszerződés azonosítója
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.FileDto> {
    const path = `/insurants/search/${args.frameId}/${args.subId}/export`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.FileDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosítottak listájának exportja
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsSearchExportByFrameContractId(
    args: {
      frameId: number,  // keretszerződés azonosítója
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.FileDto> {
    const path = `/insurants/search/${args.frameId}/export`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.FileDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosítottak keresési lista export
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurantsSearchExport(
    args: {
      name?: string,  // (optional) biztosított neve
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.FileDto> {
    const path = `/insurants/search/export`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    return this.sendRequest<models.FileDto>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * Biztosított biztosítási időszakaszainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsurancePeriodsByInsurantId(
    args: {
      insurantId: number,  // Biztosított azonosítója (NaturalPersonId)
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.InsurancePeriodDto[]> {
    const path = `/insurants/period/${args.insurantId}/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.InsurancePeriodDto[]>('GET', path, options);
  }

  /**
   * Biztosított biztosítási időszakának létrehozása
   * Response generated for [ 200 ] HTTP response code.
   */
  createInsurancePeriodByInsurantId(
    args: {
      insurantId: number,  // Biztosított azonosítója (NaturalPersonId)
      insurancePeriod: models.InsurancePeriodDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.InsurancePeriodDto[]> {
    const path = `/insurants/period/${args.insurantId}/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.InsurancePeriodDto[]>('POST', path, options, JSON.stringify(args.insurancePeriod));
  }

  /**
   * HIP felületről érkező biztosítotti adatok mentése
   * Response generated for [ 200 ] HTTP response code.
   */
  saveInsuredPersonFromHip(
    args: {
      insuredPerson: models.HIPInsuredPersonFromHIPDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPInsuredPersonFromHIPDto> {
    const path = `/insurants/hip/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.HIPInsuredPersonFromHIPDto>('PUT', path, options, JSON.stringify(args.insuredPerson));
  }

  /**
   * Biztosított lekérése a HIP felületről azonosító, ajánlati szám és fizetési szám alapján
   * Response generated for [ 200 ] HTTP response code.
   */
  getInsuredPersonFromHipQuery(
    args: {
      insuredPersonFromHIPId?: number,
      offerNumber?: string,
      paymentCode?: string,
      interruptionId?: string,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPInsuredPersonFromHIPDto> {
    const path = `/insurants/hip/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('insuredPersonFromHIPId' in args) {
      options.params = options.params.set('insuredPersonFromHIPId', String(args.insuredPersonFromHIPId));
    }
    if ('offerNumber' in args) {
      options.params = options.params.set('offerNumber', String(args.offerNumber));
    }
    if ('paymentCode' in args) {
      options.params = options.params.set('paymentCode', String(args.paymentCode));
    }
    if ('interruptionId' in args) {
      options.params = options.params.set('interruptionId', String(args.interruptionId));
    }
    return this.sendRequest<models.HIPInsuredPersonFromHIPDto>('GET', path, options);
  }

  /**
   * A munkavállaló ellenőrzése a HIP felületről
   * Response generated for [ 200 ] HTTP response code.
   */
  checkEmployee(
    args: {
      passiveKoopFrameContractId: string,
      name: string,
      birthDate: string,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPEmployeeDto> {
    const path = `/insurants/hip/employee/${args.passiveKoopFrameContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('name' in args) {
      options.params = options.params.set('name', String(args.name));
    }
    if ('birthDate' in args) {
      options.params = options.params.set('birthDate', String(args.birthDate));
    }
    return this.sendRequest<models.HIPEmployeeDto>('GET', path, options);
  }

  /**
   * Biztosított ellenőrzése, biztosítotti jogviszonyt igazoló email összeállítása és továbbítása küldésre
   * Response generated for [ 200 ] HTTP response code.
   */
  sendProofOfInsurance(
    args: {
      iD: string,  // Biztosított azonosítója (NaturalPersonId || InsuredPersonFromKoop.NaturalPersonUid)
      isPassive: boolean,  // A biztosított passzív?
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.StringResponseDto> {
    const path = `/insurants/sendProofOfInsurance/`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('iD' in args) {
      options.params = options.params.set('iD', String(args.iD));
    }
    if ('isPassive' in args) {
      options.params = options.params.set('isPassive', String(args.isPassive));
    }
    return this.sendRequest<models.StringResponseDto>('GET', path, options);
  }

  /**
   * Userhez tartozó adatok lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getProfile(
    requestHttpOptions?: HttpOptions
  ): Observable<models.ProfileDto> {
    const path = `/profile`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.ProfileDto>('GET', path, options);
  }

  /**
   * HR adminisztrátorok listájának lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getUsers(
    args: {
      meta?: models.FilteredSortedPagedDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.CirUserDto[]> {
    const path = `/users`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.CirUserDto[]>('POST', path, options, JSON.stringify(args.meta));
  }

  /**
   * HR adminisztrátor adatainak lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getUserById(
    args: {
      userId: number,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.UserDetailsDto> {
    const path = `/users/${args.userId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.UserDetailsDto>('GET', path, options);
  }

  /**
   * HR adminisztrátor szerződéshez rendelése
   * Response generated for [ 204 ] HTTP response code.
   */
  addUserToContract(
    args: {
      userId: number,
      contractId: number,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<void> {
    const path = `/users/${args.userId}/contracts/${args.contractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<void>('POST', path, options);
  }

  /**
   * HR adminisztrátor szerződésről levétele
   * Response generated for [ 204 ] HTTP response code.
   */
  removeUserFromContract(
    args: {
      userId: number,
      contractId: number,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<void> {
    const path = `/users/${args.userId}/contracts/${args.contractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<void>('DELETE', path, options);
  }

  /**
   * HR adminisztrátor létrhozása
   * Response generated for [ 200 ] HTTP response code.
   */
  createPBOUser(
    args: {
      userData?: models.CirUserDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.CirUserDto> {
    const path = `/users/create`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.CirUserDto>('POST', path, options, JSON.stringify(args.userData));
  }

  /**
   * User jelszavának érvényessége, és az url ahol megváltoztatható.
   * Response generated for [ 200 ] HTTP response code.
   */
  passwordChange(
    requestHttpOptions?: HttpOptions
  ): Observable<models.PasswordChangeDto> {
    const path = `/users/passwordChange`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.PasswordChangeDto>('GET', path, options);
  }

  /**
   * Üzenet küldése
   * Response generated for [ 204 ] HTTP response code.
   */
  postMessage(
    args: {
      koopFrameContractId?: string,
      message: models.MessageDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<void> {
    const path = `/message/send`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('koopFrameContractId' in args) {
      options.params = options.params.set('koopFrameContractId', String(args.koopFrameContractId));
    }
    return this.sendRequest<void>('POST', path, options, JSON.stringify(args.message));
  }

  /**
   * JWT token visszakérése
   * Response generated for [ 200 ] HTTP response code.
   */
  retrieveJwt(
    requestHttpOptions?: HttpOptions
  ): Observable<models.JWTDto> {
    const path = `/authorization`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.JWTDto>('GET', path, options);
  }

  /**
   * SmartCube dokumentum lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getDocument(
    args: {
      boxId: number,
      koopFrameContractId: string,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPFileDto> {
    const path = `/smartcube/document/${args.boxId}/${args.koopFrameContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.HIPFileDto>('GET', path, options);
  }

  /**
   * A művelet a megadott keretszerződésazonosító alapján összeállítja a kötéshez szükséges dokumentumok listáját. Majd előállítja az összefűzött dokumentumot.
   * Response generated for [ 200 ] HTTP response code.
   */
  getDocuments(
    args: {
      koopFrameContractId: string,
      contractGroupId: string,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPFileDto> {
    const path = `/smartcube/documents/${args.koopFrameContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    if ('contractGroupId' in args) {
      options.params = options.params.set('contractGroupId', String(args.contractGroupId));
    }
    return this.sendRequest<models.HIPFileDto>('GET', path, options);
  }

  /**
   * A művelet lekérdez és visszaad egy új captcha kódot.
   * Response generated for [ 200 ] HTTP response code.
   */
  getCaptcha(
    requestHttpOptions?: HttpOptions
  ): Observable<models.GetCaptchaStyledResultDto> {
    const path = `/onlinepayment/getcaptcha`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.GetCaptchaStyledResultDto>('GET', path, options);
  }

  /**
   * A művelet meghívja a kapott captcha kódra az ellenőrzést, és visszaadja az ellenőrzés eredményét.
   * Response generated for [ 200 ] HTTP response code.
   */
  checkCaptcha(
    args: {
      codeModel?: models.CodeModelDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.CaptchaValidationResponseDto> {
    const path = `/onlinepayment/checkcaptcha`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.CaptchaValidationResponseDto>('POST', path, options, JSON.stringify(args.codeModel));
  }

  /**
   * Adott szerződéshez tartozó kuponok lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getCoupon(
    args: {
      passiveKoopFrameContractId: string,  // Passzív keretszerződés azonosítója
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPCouponDto[]> {
    const path = `/coupon/${args.passiveKoopFrameContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.HIPCouponDto[]>('GET', path, options);
  }

  /**
   * Adott szerződéshez tartozó kupon mentése
   * Response generated for [ 200 ] HTTP response code.
   */
  saveCoupon(
    args: {
      passiveKoopFrameContractId: string,  // Passzív keretszerződés azonosítója
      couponCode: models.HIPCouponDto,
    },
    requestHttpOptions?: HttpOptions
  ): Observable<models.HIPCouponDto> {
    const path = `/coupon/${args.passiveKoopFrameContractId}`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.HIPCouponDto>('PUT', path, options, JSON.stringify(args.couponCode));
  }

  /**
   * Kompakt szerződések lekérdezése
   * Response generated for [ 200 ] HTTP response code.
   */
  getKompaktContracts(
    requestHttpOptions?: HttpOptions
  ): Observable<models.KompaktContractDto[]> {
    const path = `/kompaktContracts/search`;
    const options: APIHttpOptions = {
      ...this.options,
      ...requestHttpOptions,
    };

    return this.sendRequest<models.KompaktContractDto[]>('GET', path, options);
  }

  private sendRequest<T>(method: string, path: string, options: HttpOptions, body?: any): Observable<T> {
    switch (method) {
      case 'DELETE':
        return this.http.delete<T>(`${this.domain}${path}`, options);
      case 'GET':
        return this.http.get<T>(`${this.domain}${path}`, options);
      case 'HEAD':
        return this.http.head<T>(`${this.domain}${path}`, options);
      case 'OPTIONS':
        return this.http.options<T>(`${this.domain}${path}`, options);
      case 'PATCH':
        return this.http.patch<T>(`${this.domain}${path}`, body, options);
      case 'POST':
        return this.http.post<T>(`${this.domain}${path}`, body, options);
      case 'PUT':
        return this.http.put<T>(`${this.domain}${path}`, body, options);
      default:
        console.error(`Unsupported request: ${method}`);
        return throwError(`Unsupported request: ${method}`);
    }
  }
}
